import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  RefreshCw,
  Users,
  CalendarDays,
  CalendarClock,
  MapPin,
  Loader2,
  ShieldCheck,
  Inbox,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

const LOGO = "/fabg-logo.png";
const TOKEN_KEY = "fabg_admin_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ----------------------------- Login View ----------------------------- */
const LoginView = ({ onAuthed }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Invalid username or password");
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back", { description: "You're signed in to the FABG dashboard." });
      onAuthed(data.token);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 10%, rgba(30,102,245,0.10), transparent 55%), radial-gradient(900px 500px at 80% 0%, rgba(14,165,233,0.08), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <Card className="relative w-full max-w-md rounded-2xl border-border shadow-md">
        <div className="absolute inset-x-0 top-0 h-0.5 bg-primary rounded-t-2xl" />
        <CardHeader className="space-y-4 pt-8">
          <div className="flex items-center justify-center">
            <img src={LOGO} alt="FABG" className="h-9 w-auto" data-testid="admin-login-logo" />
          </div>
          <div className="text-center space-y-1">
            <CardTitle className="text-2xl font-display font-semibold tracking-tight">
              Admin Dashboard
            </CardTitle>
            <p className="text-sm text-muted-foreground font-body">
              Sign in to view and manage your leads
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-username">Username</Label>
              <Input
                id="admin-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="h-11 rounded-xl"
                data-testid="admin-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-11 rounded-xl"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                data-testid="admin-login-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-xl transition-colors duration-200 active:scale-[0.98]"
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" />
                  Sign in
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/* ----------------------------- Stat Card ----------------------------- */
const StatCard = ({ icon: Icon, label, value, testId, loading }) => (
  <Card className="rounded-2xl border-border shadow-sm" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-body">
            {label}
          </p>
          {loading ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-3xl font-display font-semibold tracking-tight tabular-nums text-foreground">
              {value}
            </p>
          )}
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

/* --------------------------- Dashboard View --------------------------- */
const DashboardView = ({ token, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const loadData = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError("");
      try {
        const [leadsRes, statsRes] = await Promise.all([
          fetch(`${API}/leads`, { headers: authHeaders }),
          fetch(`${API}/admin/stats`, { headers: authHeaders }),
        ]);

        if (leadsRes.status === 401 || statsRes.status === 401) {
          toast.error("Session expired", { description: "Please sign in again." });
          onLogout();
          return;
        }
        if (!leadsRes.ok) throw new Error("Failed to load leads");

        const leadsData = await leadsRes.json();
        const statsData = statsRes.ok ? await statsRes.json() : null;
        setLeads(Array.isArray(leadsData) ? leadsData : []);
        setStats(statsData);
        if (isRefresh) toast.success("Leads refreshed");
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authHeaders, onLogout]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const haystack = [
        l.first_name,
        l.last_name,
        l.email,
        l.phone,
        l.practice_name,
        l.implant_system,
        l.source,
        (l.tags || []).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, query]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      return d.toLocaleString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  };

  const regionOf = (l) =>
    (l.tags || []).map((t) => String(t).toLowerCase()).includes("usa")
      ? "USA"
      : "Canada";

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "Practice Name",
      "Implant System",
      "Outside Lab",
      "Region",
      "Source",
      "Submitted (UTC)",
    ];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = filtered.map((l) =>
      [
        l.first_name,
        l.last_name,
        l.email,
        l.phone,
        l.practice_name,
        l.implant_system,
        l.outside_lab,
        regionOf(l),
        l.source,
        l.created_at,
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fabg-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="FABG" className="h-8 w-auto" data-testid="admin-header-logo" />
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              Leads Dashboard
            </span>
          </div>
          <Button
            variant="outline"
            className="h-10 rounded-xl transition-colors duration-200"
            onClick={onLogout}
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight">
              Leads
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every application captured from your landing pages.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-10 rounded-xl transition-colors duration-200"
              onClick={() => loadData(true)}
              disabled={refreshing}
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              className="h-10 rounded-xl transition-colors duration-200 active:scale-[0.98]"
              onClick={exportCsv}
              data-testid="admin-export-csv-button"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          <StatCard
            icon={Users}
            label="Total leads"
            value={stats?.total ?? leads.length}
            loading={loading}
            testId="stat-total"
          />
          <StatCard
            icon={CalendarDays}
            label="Today"
            value={stats?.today ?? 0}
            loading={loading}
            testId="stat-today"
          />
          <StatCard
            icon={CalendarClock}
            label="Last 7 days"
            value={stats?.last_7_days ?? 0}
            loading={loading}
            testId="stat-7-days"
          />
          <StatCard
            icon={MapPin}
            label="Canada / USA"
            value={
              stats
                ? `${stats.by_region?.Canada ?? 0} / ${stats.by_region?.USA ?? 0}`
                : "—"
            }
            loading={loading}
            testId="stat-region"
          />
        </section>

        {/* Table card */}
        <Card className="rounded-2xl border-border shadow-sm">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border">
            <CardTitle className="text-base font-display font-semibold">
              All applications
              {!loading && (
                <span className="ml-2 text-sm font-body font-normal text-muted-foreground tabular-nums">
                  ({filtered.length})
                </span>
              )}
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, practice…"
                className="h-10 rounded-xl pl-9"
                data-testid="admin-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="p-8 text-center" data-testid="admin-error-state">
                <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button
                  variant="outline"
                  className="mt-4 rounded-xl"
                  onClick={() => loadData()}
                >
                  <RefreshCw className="h-4 w-4" />
                  Try again
                </Button>
              </div>
            ) : loading ? (
              <div className="p-6 space-y-3" data-testid="admin-loading-state">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center" data-testid="admin-empty-state">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary mx-auto mb-4">
                  <Inbox className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-display font-semibold text-foreground">
                  {query ? "No matching leads" : "No leads yet"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {query
                    ? "Try a different search term."
                    : "New applications will appear here automatically."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="admin-leads-table">
                  <TableHeader>
                    <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                      <TableHead className="whitespace-nowrap">Name</TableHead>
                      <TableHead className="whitespace-nowrap">Contact</TableHead>
                      <TableHead className="whitespace-nowrap">Practice</TableHead>
                      <TableHead className="whitespace-nowrap">Implant System</TableHead>
                      <TableHead className="whitespace-nowrap">Outside Lab</TableHead>
                      <TableHead className="whitespace-nowrap">Region</TableHead>
                      <TableHead className="whitespace-nowrap">Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((l, idx) => (
                      <TableRow
                        key={l.id || idx}
                        className="hover:bg-secondary/40 transition-colors duration-150"
                        data-testid={`admin-lead-row-${idx}`}
                      >
                        <TableCell className="font-medium text-foreground whitespace-nowrap">
                          {`${l.first_name || ""} ${l.last_name || ""}`.trim() || "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex flex-col">
                            <a
                              href={`mailto:${l.email}`}
                              className="text-primary hover:underline text-sm"
                            >
                              {l.email}
                            </a>
                            <span className="text-xs text-muted-foreground">{l.phone}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={l.practice_name}>
                          {l.practice_name || "—"}
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate" title={l.implant_system}>
                          {l.implant_system || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              String(l.outside_lab).toLowerCase() === "yes"
                                ? "border border-primary/30 bg-primary/10 text-primary"
                                : "border border-border bg-secondary text-slate-600"
                            }
                          >
                            {l.outside_lab || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              regionOf(l) === "USA"
                                ? "border border-amber-300 bg-amber-50 text-amber-700"
                                : "border border-border bg-secondary text-slate-700"
                            }
                          >
                            {regionOf(l)}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {formatDate(l.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

/* ------------------------------ Page ------------------------------ */
export default function AdminDashboard() {
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setChecking(false);
      return;
    }
    // Validate stored token
    fetch(`${API}/admin/me`, {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then((res) => {
        if (res.ok) {
          setToken(stored);
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return token ? (
    <DashboardView token={token} onLogout={handleLogout} />
  ) : (
    <LoginView onAuthed={setToken} />
  );
}
